// Import license initialization FIRST - before any MUI X components
import './muiLicense.js';

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { HierarchyProvider } from './contexts/HierarchyContext';
import { DashboardThemeProvider } from './components/Admin/Dashboard/dashboardTheme';
import NotificationBar from './components/NotificationBar/NotificationBar';
import ErrorBoundary from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
// EmulationBanner is now included in AdminLayout and other layout components
import UserDetail from './components/Admin/Users/UserDetail.jsx';
import AppLayout from './components/AppLayout';
import StagingBanner from './components/common/StagingBanner';
// Statically imported (MUI-only, tiny) so it can be a Suspense fallback for the
// lazy carriers chunk without pulling the carriers bundle into the main chunk.
import { CarriersPageSkeleton } from './components/Admin/Carriers/components/CarriersSkeleton';

// Lazy-loaded Pages
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const CreateShipment = lazy(() => import('./components/CreateShipment'));
const ShipmentDetail = lazy(() => import('./components/ShipmentDetail/ShipmentDetailX'));
const Shipments = lazy(() => import('./components/Shipments/ShipmentsX'));
const Tracking = lazy(() => import('./components/Tracking/Tracking'));
const Customers = lazy(() => import('./components/Customers/Customers'));
const CustomerDetail = lazy(() => import('./components/Customers/CustomerDetail'));
const Reports = lazy(() => import('./components/Reports/Reports'));
const Billing = lazy(() => import('./components/Billing/Billing'));
const Pricing = lazy(() => import('./components/Pricing/Pricing'));
const HomepageX = lazy(() => import('./components/Homepage/HomepageX/HomepageX'));
const Login = lazy(() => import('./components/Auth/Login'));
const SetPassword = lazy(() => import('./components/Auth/SetPassword'));
// Self-serve accounts: request, activation link, Google finish. The browser
// never creates an Auth account; the server does (completeSelfServeAccount).
const RequestAccount = lazy(() => import('./components/Auth/RequestAccount'));
const VerifyAccount = lazy(() => import('./components/Auth/VerifyAccount'));
const FinishSignup = lazy(() => import('./components/Auth/FinishSignup'));
const Terms = lazy(() => import('./components/Legal/Terms'));
const Privacy = lazy(() => import('./components/Legal/Privacy'));
const AddCustomer = lazy(() => import('./components/Customers/AddCustomer'));
const EditCustomer = lazy(() => import('./components/Customers/EditCustomer'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const NotificationCenter = lazy(() => import('./components/NotificationPreferences/NotificationCenter'));
const Carriers = lazy(() => import('./components/Carriers/Carriers'));
const AdminLayout = lazy(() => import('./components/Admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./components/Admin/Dashboard'));
const CompanyList = lazy(() => import('./components/Admin/Companies/CompanyList'));
const UserList = lazy(() => import('./components/Admin/Users/UserList'));
const GlobalShipmentList = lazy(() => import('./components/Admin/Shipments/GlobalShipmentList'));
const BillingDashboard = lazy(() => import('./components/Admin/Billing/BillingDashboard'));
const InvoiceForm = lazy(() => import('./components/Admin/Billing/InvoiceForm'));
const RolePermissionsView = lazy(() => import('./components/Admin/Roles/RolePermissionsView'));
const SystemSettings = lazy(() => import('./components/Admin/Settings/SystemSettings'));
const SystemConfiguration = lazy(() => import('./components/Admin/Settings/SystemConfiguration'));
const CarrierKeys = lazy(() => import('./components/Admin/Carriers/CarrierKeys'));
const EDIMapping = lazy(() => import('./components/Admin/Billing/EDIMapping'));
const AddCarrierMapping = lazy(() => import('./components/Admin/Billing/AddCarrierMapping'));
const CompanyForm = lazy(() => import('./components/Admin/Companies/CompanyForm'));
const CompanyDetail = lazy(() => import('./components/Admin/Companies/CompanyDetail'));
const UserForm = lazy(() => import('./components/Admin/Users/UserForm'));

// Organization Components (New)
const OrganizationList = lazy(() => import('./components/Admin/Organizations/OrganizationList'));
const OrganizationForm = lazy(() => import('./components/Admin/Organizations/OrganizationForm'));
const OrganizationDetail = lazy(() => import('./components/Admin/Organizations/OrganizationDetail'));

// New Admin Carriers component
const AdminCarriers = lazy(() => import('./components/Admin/Carriers/Carriers'));
const AddCarrier = lazy(() => import('./components/Admin/Carriers/AddCarrier'));
const EditCarrier = lazy(() => import('./components/Admin/Carriers/EditCarrier'));
const APIPlayground = lazy(() => import('./components/Admin/APISandbox/APIPlayground'));
const CarrierSandbox = lazy(() => import('./components/Admin/APISandbox/APISandbox'));


// Admin Customers Components (New)
const AdminCustomerList = lazy(() => import('./components/Admin/Customers/CustomerList'));
const AdminCustomerDetail = lazy(() => import('./components/Admin/Customers/CustomerDetail'));
const AdminCustomerForm = lazy(() => import('./components/Admin/Customers/CustomerForm'));

// Admin Brokers Components (New)
// Unified admin customs-brokers page; BrokerForm and BrokerDetail retired —
// add/edit/adopt are handled via inline dialog in BrokerList.
const BrokerList = lazy(() => import('./components/Admin/Brokers/BrokerList'));

// Markups Page (New)
const MarkupsPage = lazy(() => import('./components/Admin/Markups/MarkupsPage'));
const ResellerInvoicesPage = lazy(() => import('./components/Admin/ResellerInvoices/ResellerInvoicesPage'));

// Integrations Page (New)
const IntegrationsPage = lazy(() => import('./components/Admin/Integrations/IntegrationsPage'));

// API & Webhooks Management
const ApiKeyManagement = lazy(() => import('./components/Admin/ApiKeys/ApiKeyManagement'));
const ApiKeyDetail = lazy(() => import('./components/Admin/ApiKeys/ApiKeyDetail'));
const Atlas2 = lazy(() => import('./components/Admin/Atlas/Atlas2'));
const ApiDocumentation = lazy(() => import('./components/Admin/ApiDocs/ApiDocumentation'));
const SolushipMcpDocs = lazy(() => import('./components/Admin/ApiDocs/SolushipMcpDocs'));
const WebhookManager = lazy(() => import('./components/Admin/Webhooks/WebhookManager'));
const DeveloperPortal = lazy(() => import('./components/Admin/DeveloperPortal/DeveloperPortal'));
const DeveloperPortalV2 = lazy(() => import('./components/Admin/DeveloperPortalV2/DeveloperPortalV2'));
const SolushipPlaygroundV2 = lazy(() => import('./components/Admin/DeveloperPortalV2/SolushipPlaygroundV2'));
const DataSyncDashboard = lazy(() => import('./components/Admin/DeveloperPortalV2/DataSync/DataSyncDashboard'));
const DataSyncDetail = lazy(() => import('./components/Admin/DeveloperPortalV2/DataSync/DataSyncDetail'));
const Mcp2Docs = lazy(() => import('./components/Admin/DeveloperPortalV2/Mcp2Docs'));







// New Edit Carrier Mapping component
const EditCarrierMapping = lazy(() => import('./components/Admin/Billing/EditCarrierMapping'));

// Admin Addresses component
const GlobalAddressList = lazy(() => import('./components/Admin/Addresses/GlobalAddressList'));
const CarrierAccounts = lazy(() => import('./components/Admin/CarrierAccounts/CarrierAccounts'));

// Email Notification Preferences component
const NotificationPreferences = lazy(() => import('./components/NotificationPreferences/NotificationPreferences'));

// Follow-ups Dashboard component
const FollowUpsDashboard = lazy(() => import('./components/FollowUps/FollowUpsDashboard'));

// Booking reconcile queue (bookings whose carrier outcome is unknown)
const BookingReconcileQueue = lazy(() => import('./components/Admin/BookingReconcile/BookingReconcileQueue'));

// Loading Component
const LoadingFallback = () => (
    <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
    }}>
        <CircularProgress />
    </Box>
);

// Conditional NotificationBar - only show on public pages
const ConditionalNotificationBar = () => {
    const location = useLocation();
    // "/", /login and /signup are left out: they draw their own header.
    const publicRoutes = ['/pricing'];
    const isPublicRoute = publicRoutes.includes(location.pathname);
    
    return isPublicRoute ? <NotificationBar /> : null;
};

// The admin dashboard renders in the brand palette (blue accent, grey neutrals,
// system font); see dashboardTheme.js.
const AdminDashboardRoute = () => (
    <DashboardThemeProvider palette="brand">
        <AdminDashboard />
    </DashboardThemeProvider>
);

// Route Change Handler for Globe cleanup
const RouteChangeHandler = () => {
    const location = useLocation();
    const prevLocationRef = React.useRef(location.pathname);
    
    React.useEffect(() => {
        const currentPath = location.pathname;
        const prevPath = prevLocationRef.current;
        
        // If we're navigating away from dashboard, force cleanup
        if (prevPath === '/dashboard' && currentPath !== '/dashboard') {
            console.log('🧹 Route Change: Navigating away from Dashboard - forcing Globe cleanup');
            
            // Force garbage collection if available (development only)
            if (typeof window !== 'undefined' && window.gc && process.env.NODE_ENV === 'development') {
                setTimeout(() => {
                    window.gc();
                    console.log('🗑️ Forced garbage collection after leaving Dashboard');
                }, 500);
            }
        }
        
        prevLocationRef.current = currentPath;
    }, [location.pathname]);
    
    return null;
};

function AppRoutes() {
    return (
        <Suspense fallback={<LoadingFallback />}>
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomepageX />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<RequestAccount />} />
                <Route path="/signup/verify" element={<VerifyAccount />} />
                <Route path="/signup/finish" element={<FinishSignup />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />

                {/* Protected Routes */}
                {/* /dashboard retired — all traffic routes to the /admin universe */}
                <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
                <Route path="/shipments" element={
                    <ProtectedRoute>
                        <Shipments />
                    </ProtectedRoute>
                } />
                <Route path="/create-shipment" element={
                    <ProtectedRoute>
                        <CreateShipment />
                    </ProtectedRoute>
                } />
                <Route path="/create-shipment/:step" element={
                    <ProtectedRoute>
                        <CreateShipment />
                    </ProtectedRoute>
                } />
                <Route path="/create-shipment/:step/:draftId" element={
                    <ProtectedRoute>
                        <CreateShipment />
                    </ProtectedRoute>
                } />
                {/* Public Tracking Routes - Anyone can access these */}
                <Route path="/tracking" element={<Tracking />} />
                <Route path="/tracking/:trackingIdentifier" element={<Tracking />} />
                <Route path="/customers" element={
                    <ProtectedRoute>
                        <Customers />
                    </ProtectedRoute>
                } />
                <Route path="/customers/new" element={
                    <ProtectedRoute>
                        <AddCustomer />
                    </ProtectedRoute>
                } />
                <Route path="/customers/:id" element={
                    <ProtectedRoute>
                        <CustomerDetail />
                    </ProtectedRoute>
                } />
                <Route path="/customers/:id/edit" element={
                    <ProtectedRoute>
                        <EditCustomer />
                    </ProtectedRoute>
                } />
                <Route path="/reports" element={
                    <ProtectedRoute>
                        <Reports />
                    </ProtectedRoute>
                } />
                {/* /billing is the CUSTOMER-facing invoice surface (AdminHeader's
                    "My Invoices"), reachable by any VIEW_BILLING role — unlike
                    /admin/billing, which is the back-office dashboard gated on
                    VIEW_ALL_INVOICES. It lives outside /admin/* but must still render
                    the app chrome, so it gets AdminLayout (header + Outlet) behind a
                    ProtectedRoute rather than AdminRoute. Without the layout the page
                    rendered bare, with no header to navigate away from. */}
                <Route path="/billing" element={
                    <ProtectedRoute>
                        <AdminLayout />
                    </ProtectedRoute>
                }>
                    <Route index element={<Billing />} />
                </Route>
                <Route path="/profile" element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                } />
                <Route path="/shipment/:id" element={
                    <ProtectedRoute>
                        <ShipmentDetail />
                    </ProtectedRoute>
                } />
                <Route path="/carriers" element={
                    <ProtectedRoute>
                        <Carriers />
                    </ProtectedRoute>
                } />
                <Route path="/notifications" element={
                    <ProtectedRoute>
                        <NotificationPreferences />
                    </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/admin/*" element={
                    <AdminRoute>
                        <AdminLayout />
                    </AdminRoute>
                }>
                    <Route index element={<AdminDashboardRoute />} />
                    <Route path="dashboard" element={<AdminDashboardRoute />} />
                    <Route path="companies" element={<CompanyList />} />
                    <Route path="companies/new" element={<CompanyForm />} />
                    <Route path="companies/:id" element={<CompanyDetail />} />
                    <Route path="companies/:id/edit" element={<CompanyForm />} />
                    <Route path="customers" element={<AdminCustomerList />} />
                    <Route path="customers/new" element={<AdminCustomerForm />} />
                    <Route path="customers/:id" element={<AdminCustomerDetail />} />
                    <Route path="customers/:id/edit" element={<AdminCustomerForm />} />
                    <Route path="brokers" element={<BrokerList />} />
                    {/* new / :id / :id/edit routes retired — inline dialog handles all CRUD */}
                    <Route path="organizations" element={<OrganizationList />} />
                    <Route path="organizations/new" element={<OrganizationForm />} />
                    <Route path="organizations/:id" element={<OrganizationDetail />} />
                    <Route path="organizations/:id/edit" element={<OrganizationForm />} />
                    <Route path="users" element={<UserList />} />
                    <Route path="users/new" element={<UserForm />} />
                    <Route path="users/:id" element={<UserDetail />} />
                    <Route path="users/:id/edit" element={<UserForm />} />
                    <Route path="shipments/*" element={<GlobalShipmentList />} />
                    <Route path="shipment/:id" element={<ShipmentDetail />} />
                    <Route path="billing" element={<BillingDashboard initialTab="invoices" />} />
                    <Route path="billing/ready-to-invoice" element={<BillingDashboard initialTab="ready-to-invoice" />} />
                    <Route path="billing/ready-to-deliver" element={<BillingDashboard initialTab="ready-to-deliver" />} />
                    <Route path="billing/invoice-exceptions" element={<BillingDashboard initialTab="invoice-exceptions" />} />
                    <Route path="billing/orphans" element={<BillingDashboard initialTab="orphans" />} />
                    <Route path="billing/invoiced-no-carrier-bill" element={<BillingDashboard initialTab="invoiced-no-carrier-bill" />} />
                    <Route path="billing/charges" element={<BillingDashboard initialTab="charges" />} />
                    <Route path="billing/invoice/new" element={<InvoiceForm />} />
                    <Route path="billing/invoice/:id" element={<InvoiceForm />} />
                    <Route path="billing/ap-processing" element={<BillingDashboard initialTab="ap-processing" />} />
                    <Route path="billing/generate" element={<BillingDashboard initialTab="bulk" />} />
                    <Route path="billing/business" element={<BillingDashboard initialTab="business" />} />
                    <Route path="billing/payments" element={<BillingDashboard initialTab="payments" />} />
                    <Route path="billing/commissions" element={<BillingDashboard initialTab="commissions" />} />
                    <Route path="billing/payment-terms" element={<BillingDashboard initialTab="payment-terms" />} />
                    <Route path="billing/payment-methods" element={<BillingDashboard initialTab="payment-methods" />} />
                    <Route path="role-permissions/*" element={<RolePermissionsView />} />
                    <Route path="settings" element={<SystemSettings />} />
                    <Route path="configuration" element={<SystemConfiguration />} />
                    <Route path="carrier-keys" element={<CarrierKeys />} />
                    <Route path="carriers" element={<Suspense fallback={<CarriersPageSkeleton />}><AdminCarriers /></Suspense>} />
                    <Route path="carriers/new" element={<AddCarrier />} />
                    <Route path="carriers/:carrierId/edit" element={<EditCarrier />} />
                    <Route path="api-sandbox" element={<APIPlayground />} />
                    <Route path="carrier-sandbox" element={<CarrierSandbox />} />
                    <Route path="api-keys" element={<ApiKeyManagement />} />
                    <Route path="api-keys/:keyId" element={<ApiKeyDetail />} />
                    <Route path="api-docs" element={<ApiDocumentation />} />
                    <Route path="api-docs/mcp" element={<SolushipMcpDocs />} />
                    <Route path="webhooks" element={<WebhookManager />} />
                    <Route path="developer-portal" element={<DeveloperPortal />} />
                    <Route path="atlas-2-0" element={<Atlas2 />} />
                    <Route path="developer-portal-v2" element={<DeveloperPortalV2 />} />
                    <Route path="developer-portal-v2/mcp" element={<Mcp2Docs />} />
                    <Route path="developer-portal-v2/playground" element={<SolushipPlaygroundV2 />} />
                    <Route path="developer-portal-v2/playground/:endpointId" element={<SolushipPlaygroundV2 />} />
                    <Route path="developer-portal-v2/data-sync" element={<DataSyncDashboard />} />
                    <Route path="developer-portal-v2/data-sync/:connectionId" element={<DataSyncDetail />} />
                    <Route path="integrations" element={<IntegrationsPage />} />
                    <Route path="carrier-accounts" element={<CarrierAccounts />} />
                    <Route path="markups" element={<MarkupsPage />} />
                    <Route path="markups/:tab" element={<MarkupsPage />} />
                    <Route path="reseller-invoices" element={<ResellerInvoicesPage />} />
                    <Route path="reseller-invoices/:tab" element={<ResellerInvoicesPage />} />
                    <Route path="addresses" element={<GlobalAddressList />} />
                    <Route path="followups" element={<FollowUpsDashboard />} />
                    <Route path="booking-reconcile" element={<BookingReconcileQueue />} />
                    <Route path="profile" element={<Profile isModal={false} />} />
                    <Route path="notifications" element={<NotificationCenter isModal={false} />} />
                    {/* Any other /admin path (a removed page, a typo) goes to the admin home.
                        Without this the /admin/* parent matched it and drew an empty outlet;
                        the app-level catch-all below never sees /admin URLs. */}
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                </Route>

                {/* Fallback Route */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

// Minimal theme: the app otherwise relies on MUI defaults, so we override ONLY the semantic
// `warning` palette from MUI's default orange (#ed6c02) to yellow — the brand rule forbids orange.
// This recolors every color="warning"/severity="warning" (Alerts, Chips, Buttons, icons) + notistack
// warning toasts app-wide in one place, without changing any other default.
const appTheme = createTheme({
    palette: {
        warning: { main: '#eab308', light: '#facc15', dark: '#a16207', contrastText: '#ffffff' },
    },
});

function App() {
    return (
        <ThemeProvider theme={appTheme}>
        <StagingBanner />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <SnackbarProvider>
                <Router>
                    <AuthProvider>
                        <CompanyProvider>
                            <HierarchyProvider>
                                <CssBaseline />
                                <ErrorBoundary>
                                    <ConditionalNotificationBar />
                                    <RouteChangeHandler />
                                    <AppLayout>
                                        <AppRoutes />
                                    </AppLayout>
                                </ErrorBoundary>
                            </HierarchyProvider>
                        </CompanyProvider>
                    </AuthProvider>
                </Router>
            </SnackbarProvider>
        </LocalizationProvider>
        </ThemeProvider>
    );
}

export default App; 